import { add } from 'date-fns';

import { TIME_UNIT_OPTIONS } from '../constants';
import { ScheduleDateOrRelative, ScheduleIncrement, ScheduleRelativeDate } from '../types';
import { fromAbsoluteDateString } from './date-time';

export function isRelativeDate(
  value: ScheduleDateOrRelative | undefined,
): value is ScheduleRelativeDate {
  return (
    typeof value === 'object' &&
    value !== null &&
    (value.reference === 'now' || value.reference === 'previous')
  );
}

/** A relative date with no increments represents "now" (immediate execution/start). */
export function isImmediate(value: ScheduleDateOrRelative | undefined): boolean {
  return isRelativeDate(value) && (!value.increments || value.increments.length === 0);
}

/** Applies each increment sequentially (array order affects the result, e.g. months before days). */
export function applyIncrements(base: Date, increments: ScheduleIncrement[] = []): Date {
  return increments.reduce(
    (current, increment) => add(current, { [increment.unit]: increment.value }),
    base,
  );
}

/** Resolves the preview instant a {@link ScheduleRelativeDate} points to, given a reference `now`. */
export function resolveRelativeDate(relative: ScheduleRelativeDate, now: Date = new Date()): Date {
  return applyIncrements(now, relative.increments);
}

/** Resolves any {@link ScheduleDateOrRelative} (absolute string or relative) into a concrete instant. */
export function resolveDateOrRelative(
  value: ScheduleDateOrRelative | undefined,
  timeZone: string,
  now: Date = new Date(),
): Date | undefined {
  if (!value) return undefined;
  return typeof value === 'string'
    ? fromAbsoluteDateString(value, timeZone)
    : resolveRelativeDate(value, now);
}

/** Human-readable summary of an ordered increments list, e.g. "1 mes, 2 días y 30 minutos". */
export function describeIncrements(increments: ScheduleIncrement[] = []): string {
  const parts = increments
    .filter((increment) => increment.value > 0)
    .map((increment) => {
      const unitOption = TIME_UNIT_OPTIONS.find((option) => option.value === increment.unit);
      const unitLabel = unitOption
        ? increment.value === 1
          ? unitOption.label
          : unitOption.pluralLabel
        : increment.unit;
      return `${increment.value} ${unitLabel}`;
    });

  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0];
  return `${parts.slice(0, -1).join(', ')} y ${parts[parts.length - 1]}`;
}
